/**
 * Invoice (UPLOAD purchase order) vs Delivery Note quantity comparison by barcode.
 * Invoice units = deliveryQuantity * boxSize (same as stock calc on PO).
 * DN net units = actualOrderAmount − damagedQuantity (good received qty).
 *
 * Only-on-DN dispositions (invoice confirm):
 * ACCEPTED — no action
 * SCANNING_ERROR — reverse stock, no return, no DN update
 * WRONG_ITEM — open return, no DN update, no stock reverse
 */

export const DN_ONLY_DISPOSITION = {
    ACCEPTED: 'ACCEPTED',
    SCANNING_ERROR: 'SCANNING_ERROR',
    WRONG_ITEM: 'WRONG_ITEM'
};

export const DN_ONLY_DISPOSITION_OPTIONS = [
    {name: 'Accepted', value: DN_ONLY_DISPOSITION.ACCEPTED},
    {name: 'Scanning error', value: DN_ONLY_DISPOSITION.SCANNING_ERROR},
    {name: 'Wrong item', value: DN_ONLY_DISPOSITION.WRONG_ITEM}
];

function toQtyNumber(value) {
    if (value == null || value === '') {
        return 0;
    }
    const number = Number(value);
    return Number.isNaN(number) ? 0 : number;
}

function invoiceLineUnits(item) {
    const deliveryQuantity = toQtyNumber(item.deliveryQuantity);
    const boxSize = toQtyNumber(item.boxSize);
    if (boxSize > 0) {
        return deliveryQuantity * boxSize;
    }
    return deliveryQuantity;
}

function clampNonNegative(value) {
    return value > 0 ? value : 0;
}

export function normalizeDnOnlyDisposition(value) {
    const raw = value != null ? String(value).trim().toUpperCase() : '';
    if (raw === DN_ONLY_DISPOSITION.SCANNING_ERROR || raw === DN_ONLY_DISPOSITION.WRONG_ITEM) {
        return raw;
    }
    return DN_ONLY_DISPOSITION.ACCEPTED;
}

export function buildInvoiceDeliveryNoteComparisonRows(invoiceItems, deliveryNoteItems, dispositionsByBarcode = null) {
    const invoiceByBarcode = {};
    (invoiceItems || []).forEach(item => {
        if (item?.status && item.status !== 'ACTIVE') {
            return;
        }
        const barcode = String(item?.barcode || '').trim();
        if (!barcode) {
            return;
        }
        if (!invoiceByBarcode[barcode]) {
            invoiceByBarcode[barcode] = {
                barcode,
                itemName: item.itemName || '',
                invoiceQty: 0
            };
        }
        invoiceByBarcode[barcode].invoiceQty += invoiceLineUnits(item);
        if (!invoiceByBarcode[barcode].itemName && item.itemName) {
            invoiceByBarcode[barcode].itemName = item.itemName;
        }
    });

    const dnByBarcode = {};
    (deliveryNoteItems || []).forEach(item => {
        const barcode = String(item?.barcode || '').trim();
        if (!barcode) {
            return;
        }
        if (!dnByBarcode[barcode]) {
            dnByBarcode[barcode] = {
                barcode,
                itemName: item.itemName || '',
                deliveredQty: 0,
                damagedQty: 0
            };
        }
        dnByBarcode[barcode].deliveredQty += toQtyNumber(item.actualOrderAmount);
        dnByBarcode[barcode].damagedQty += toQtyNumber(item.damagedQuantity);
        if (!dnByBarcode[barcode].itemName && item.itemName) {
            dnByBarcode[barcode].itemName = item.itemName;
        }
    });

    const barcodes = new Set([...Object.keys(invoiceByBarcode), ...Object.keys(dnByBarcode)]);
    const statusOrder = {UNEXPECTED: 0, MISSING: 1, SHORT: 2, OVER: 3, MATCHED: 4};
    const rows = [];

    barcodes.forEach(barcode => {
        const invoice = invoiceByBarcode[barcode];
        const dn = dnByBarcode[barcode];
        const invoiceQty = invoice ? invoice.invoiceQty : 0;
        const deliveredQty = dn ? dn.deliveredQty : 0;
        const damagedQty = dn ? dn.damagedQty : 0;
        const netQty = clampNonNegative(deliveredQty - damagedQty);
        let matchStatus = 'MATCHED';
        let warning = '';

        if (!invoice && dn) {
            matchStatus = 'UNEXPECTED';
            warning = 'On Delivery Note but not on invoice';
        } else if (invoice && !dn) {
            matchStatus = 'MISSING';
            warning = `Invoice ${invoiceQty}, nothing on Delivery Note`;
        } else if (netQty < invoiceQty) {
            matchStatus = 'SHORT';
            warning = `Invoice ${invoiceQty}, Net DN ${netQty}`
                + (damagedQty > 0 ? ` (delivered ${deliveredQty}, rejected ${damagedQty})` : '');
        } else if (netQty > invoiceQty) {
            matchStatus = 'OVER';
            warning = `Invoice ${invoiceQty}, Net DN ${netQty}`
                + (damagedQty > 0 ? ` (delivered ${deliveredQty}, rejected ${damagedQty})` : '');
        }

        const isOnlyOnDn = matchStatus === 'UNEXPECTED';
        const disposition = isOnlyOnDn
            ? normalizeDnOnlyDisposition(dispositionsByBarcode?.[barcode])
            : null;

        rows.push({
            id: barcode,
            barcode,
            itemName: (invoice && invoice.itemName) || (dn && dn.itemName) || '',
            invoiceQty,
            deliveredQty,
            damagedQty,
            netQty,
            difference: netQty - invoiceQty,
            matchStatus,
            warning,
            isOnlyOnDn,
            disposition
        });
    });

    rows.sort((a, b) => (statusOrder[a.matchStatus] ?? 9) - (statusOrder[b.matchStatus] ?? 9)
        || String(a.barcode).localeCompare(String(b.barcode)));
    return rows;
}

export function countInvoiceDnComparisonIssues(rows) {
    return (rows || []).filter(row => row.matchStatus && row.matchStatus !== 'MATCHED').length;
}

/**
 * Invoice billed more than good-received DN qty (Invoice > Net DN).
 * Drives Open Return on confirm: return qty = invoiceQty − netQty.
 * Existing flow for Only-on-invoice create return (and SHORT) — kept as-is.
 */
export function getInvoiceShortageRows(rows) {
    return (rows || []).filter(row => {
        const status = row?.matchStatus;
        if (status !== 'SHORT' && status !== 'MISSING') {
            return false;
        }
        const invoiceQty = toQtyNumber(row.invoiceQty);
        const netQty = toQtyNumber(row.netQty);
        return invoiceQty > netQty;
    });
}

export function buildInvoiceShortageConfirmMessage(rows) {
    const shortageRows = getInvoiceShortageRows(rows);
    if (!shortageRows.length) {
        return '';
    }
    const lines = shortageRows.slice(0, 8).map(row => {
        const name = row.itemName || row.barcode || '';
        const returnQty = toQtyNumber(row.invoiceQty) - toQtyNumber(row.netQty);
        return `• ${name}: Invoice ${row.invoiceQty}, Net DN ${row.netQty}, Return ${returnQty}`;
    });
    const more = shortageRows.length > 8 ? `\n… and ${shortageRows.length - 8} more` : '';
    return `Invoice exceeds Delivery Note (net) for ${shortageRows.length} product(s):\n${lines.join('\n')}${more}`;
}

/** Only-on-DN rows with a non-default action (applied on invoice confirm). */
export function buildInvoiceDnResolutionPayload(rows) {
    return (rows || [])
        .filter(row => row?.isOnlyOnDn || row?.matchStatus === 'UNEXPECTED')
        .map(row => ({
            barcode: row.barcode,
            disposition: normalizeDnOnlyDisposition(row.disposition)
        }))
        .filter(row => row.disposition !== DN_ONLY_DISPOSITION.ACCEPTED);
}

export function countPendingOnlyOnDnActions(rows) {
    return (rows || []).filter(row =>
        (row?.isOnlyOnDn || row?.matchStatus === 'UNEXPECTED')
        && normalizeDnOnlyDisposition(row.disposition) !== DN_ONLY_DISPOSITION.ACCEPTED
    ).length;
}
